import { useEffect, useRef, useState } from 'react';

type PdfDoc = { numPages: number; getPage(n: number): Promise<PdfPageProxy> };
type PdfPageProxy = { getViewport(o: { scale: number }): { width: number; height: number }; render(o: { canvas: HTMLCanvasElement; viewport: unknown }): { promise: Promise<void> } };

/** Un documento abierto por dirección (la consola y la pantalla pasan página tras página del mismo PDF). */
const docs = new Map<string, Promise<PdfDoc>>();

async function openPdf(url: string): Promise<PdfDoc> {
  let doc = docs.get(url);
  if (!doc) {
    doc = (async () => {
      // pdf.js se carga solo cuando hay un PDF que proyectar.
      const pdfjs = await import('pdfjs-dist');
      // Como .js (no .mjs): el servidor web lo entrega con el tipo correcto.
      const worker = await import('pdfjs-dist/build/pdf.worker.min.mjs?worker&url');
      pdfjs.GlobalWorkerOptions.workerSrc = worker.default;
      return (await pdfjs.getDocument({ url, withCredentials: true }).promise) as unknown as PdfDoc;
    })();
    docs.set(url, doc);
    doc.catch(() => docs.delete(url));
  }
  return doc;
}

/** Una página de un PDF, ajustada a la diapositiva (16:9) sin deformarla. */
export function PdfPage({ url, page, className }: { url: string; page: number; className?: string }) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let canceled = false;
    setFailed(false);
    (async () => {
      const doc = await openPdf(url);
      const p = await doc.getPage(Math.min(Math.max(page, 1), doc.numPages));
      const el = canvas.current;
      if (canceled || !el) return;
      // Resolución según el tamaño en pantalla (nítido en el proyector, ligero en miniaturas).
      const box = el.parentElement?.getBoundingClientRect();
      const base = p.getViewport({ scale: 1 });
      const scale = Math.max(0.5, Math.min(((box?.width || 1280) * (window.devicePixelRatio || 1)) / base.width, ((box?.height || 720) * (window.devicePixelRatio || 1)) / base.height, 4));
      const viewport = p.getViewport({ scale });
      el.width = Math.floor(viewport.width);
      el.height = Math.floor(viewport.height);
      await p.render({ canvas: el, viewport }).promise;
    })().catch(() => {
      if (!canceled) setFailed(true);
    });
    return () => {
      canceled = true;
    };
  }, [url, page]);
  if (failed) return <p className="slide__text">No se pudo mostrar el PDF.</p>;
  return <canvas ref={canvas} className={className} aria-label={`Página ${page}`} />;
}
